import React, { useEffect, useState } from "react";
import AppShell from "../components/AppShell";
import { api } from "../lib/api";
import { toast } from "sonner";
import { UsersThree, Plus, Trash, Upload } from "@phosphor-icons/react";

const empty = { name: "", email: "", phone: "", company: "", states: "", zip_codes: "", property_types: [], min_price: 0, max_price: 5000000, notes: "", active: true };
const emptyBulk = { emails: "", state: "TX", property_types: ["Residential", "Land"], min_price: 0, max_price: 5000000, company: "" };

export default function Buyers() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [bulk, setBulk] = useState(emptyBulk);
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = async () => {
    try { const r = await api.get("/buyers"); setRows(r.data); }
    catch { toast.error("Failed to load buyers"); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const body = {
      ...form,
      min_price: Number(form.min_price) || 0,
      max_price: Number(form.max_price) || 5000000,
      states: (typeof form.states === "string" ? form.states.split(",").map(x => x.trim().toUpperCase()).filter(Boolean) : form.states),
      zip_codes: (typeof form.zip_codes === "string" ? form.zip_codes.split(",").map(x => x.trim()).filter(Boolean) : form.zip_codes) || [],
      property_types: form.property_types || [],
    };
    try {
      if (editing) {
        await api.patch(`/buyers/${editing}`, body);
        toast.success("Buyer updated");
      } else {
        await api.post("/buyers", body);
        toast.success("Buyer added");
      }
      setForm(empty); setEditing(null); load();
    } catch { toast.error("Save failed"); }
  };

  const submitBulk = async (e) => {
    e.preventDefault();
    const emails = (bulk.emails || "").split(/[\n,;]/).map(s => s.trim()).filter(Boolean);
    if (emails.length === 0) { toast.error("Paste at least one email address"); return; }
    setBulkBusy(true);
    try {
      const r = await api.post("/buyers/bulk-import", {
        emails,
        state: bulk.state,
        property_types: bulk.property_types,
        min_price: Number(bulk.min_price) || 0,
        max_price: Number(bulk.max_price) || 5000000,
        company: bulk.company || null,
      });
      const { imported, skipped_duplicates, total_parsed } = r.data;
      toast.success(`Imported ${imported} buyers · ${skipped_duplicates} duplicates skipped · ${total_parsed} valid emails parsed`);
      setBulk(emptyBulk); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk import failed");
    } finally { setBulkBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this buyer?")) return;
    try { await api.delete(`/buyers/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  const edit = (b) => {
    setEditing(b.id);
    setForm({
      ...b,
      states: (b.states || []).join(", "),
      zip_codes: (b.zip_codes || []).join(", "),
      property_types: b.property_types || [],
    });
  };

  const togglePType = (t) => {
    const cur = new Set(form.property_types || []);
    if (cur.has(t)) cur.delete(t); else cur.add(t);
    setForm({ ...form, property_types: Array.from(cur) });
  };

  const toggleBulkPType = (t) => {
    const cur = new Set(bulk.property_types || []);
    if (cur.has(t)) cur.delete(t); else cur.add(t);
    setBulk({ ...bulk, property_types: Array.from(cur) });
  };

  return (
    <AppShell header={<div className="border-b border-zinc-800 px-6 py-4"><div className="overline">Counterparties</div><h1 className="text-lg font-semibold flex items-center gap-2"><UsersThree size={18} /> Counterparty List</h1></div>}>
      <div className="p-4 lg:p-6 grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
        <div className="space-y-4">
          <form onSubmit={submit} className="border border-zinc-800 rounded-sm p-4 space-y-3 bg-[#0b0b0e]" data-testid="buyer-form">
            <div className="overline">{editing ? "Edit buyer" : "Add cash buyer"}</div>
            <Input label="Name" value={form.name} onChange={v => setForm({...form, name:v})} required testid="buyer-name" />
            <Input label="Email" type="email" value={form.email} onChange={v => setForm({...form, email:v})} required testid="buyer-email" />
            <div className="grid grid-cols-2 gap-2">
              <Input label="Phone" value={form.phone || ""} onChange={v => setForm({...form, phone:v})} testid="buyer-phone" />
              <Input label="Company" value={form.company || ""} onChange={v => setForm({...form, company:v})} testid="buyer-company" />
            </div>
            <Input label="States (comma-separated)" value={form.states} onChange={v => setForm({...form, states:v})} placeholder="TX, FL" testid="buyer-states" />
            <Input label="ZIP codes (comma-separated, optional)" value={form.zip_codes} onChange={v => setForm({...form, zip_codes:v})} placeholder="77002, 33101" testid="buyer-zips" />
            <div>
              <div className="overline mb-1.5">Property types</div>
              <div className="flex flex-wrap gap-2">
                {["Land","Residential","Commercial"].map(t => {
                  const on = (form.property_types || []).includes(t);
                  return (
                    <button key={t} type="button" onClick={() => togglePType(t)} data-testid={`buyer-ptype-${t.toLowerCase()}`}
                      className={`text-[11px] mono uppercase tracking-[0.15em] px-2.5 py-1.5 rounded-sm border ${on ? "bg-emerald-500/15 border-emerald-500/50 text-emerald-300" : "border-zinc-800 text-zinc-500 hover:border-zinc-600"}`}>
                      {t}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Input label="Min price $" type="number" value={form.min_price} onChange={v => setForm({...form, min_price:v})} testid="buyer-min-price" />
              <Input label="Max price $" type="number" value={form.max_price} onChange={v => setForm({...form, max_price:v})} testid="buyer-max-price" />
            </div>
            <div>
              <div className="overline mb-1.5">Notes</div>
              <textarea value={form.notes || ""} onChange={e => setForm({...form, notes:e.target.value})} rows={2}
                data-testid="buyer-notes"
                className="w-full bg-[#18181b] hairline px-2 py-1.5 mono text-xs rounded-sm focus:outline-none focus:border-emerald-500"/>
            </div>
            <label className="flex items-center gap-2 text-sm text-zinc-300 select-none">
              <input type="checkbox" checked={!!form.active} onChange={e => setForm({...form, active: e.target.checked})} data-testid="buyer-active" />
              Active — receive buyer-blast emails
            </label>
            <div className="flex gap-2">
              <button type="submit" data-testid="buyer-submit"
                className="text-xs mono uppercase tracking-[0.15em] px-3 py-2 rounded-sm bg-emerald-500 hover:bg-emerald-400 text-black font-semibold flex items-center gap-2">
                <Plus size={13} weight="bold" /> {editing ? "Save" : "Add"}
              </button>
              {editing && (
                <button type="button" onClick={() => {setEditing(null); setForm(empty);}} data-testid="buyer-cancel"
                  className="text-xs mono uppercase tracking-[0.15em] px-3 py-2 rounded-sm border border-zinc-700 hover:border-zinc-500">
                  Cancel
                </button>
              )}
            </div>
          </form>

          <form onSubmit={submitBulk} className="border border-emerald-500/40 rounded-sm p-4 space-y-3 bg-emerald-500/[0.03]" data-testid="buyer-bulk-form">
            <div className="overline flex items-center gap-1.5 text-emerald-300"><Upload size={12} weight="bold" /> Bulk import investor emails</div>
            <p className="text-[11px] text-zinc-500 mono">Paste one email per line (or comma/semicolon separated). Each becomes an active buyer tagged with the chosen state.</p>
            <textarea
              value={bulk.emails}
              onChange={e => setBulk({ ...bulk, emails: e.target.value })}
              rows={6}
              placeholder={"investor1@example.com\ninvestor2@example.com\ntx.flipper@example.com"}
              data-testid="buyer-bulk-emails"
              className="w-full bg-[#18181b] hairline px-2 py-1.5 mono text-xs rounded-sm focus:outline-none focus:border-emerald-500"
            />
            <div className="grid grid-cols-2 gap-2">
              <div>
                <div className="overline mb-1">State tag</div>
                <select
                  value={bulk.state}
                  onChange={e => setBulk({ ...bulk, state: e.target.value })}
                  data-testid="buyer-bulk-state"
                  className="w-full bg-[#18181b] hairline px-2 py-1.5 mono text-xs rounded-sm focus:outline-none focus:border-emerald-500">
                  <option value="TX">TX</option>
                  <option value="FL">FL</option>
                  <option value="GA">GA</option>
                  <option value="NC">NC</option>
                  <option value="SC">SC</option>
                  <option value="TN">TN</option>
                </select>
              </div>
              <Input label="Company (optional)" value={bulk.company} onChange={v => setBulk({ ...bulk, company: v })} testid="buyer-bulk-company" />
            </div>
            <div>
              <div className="overline mb-1.5">Property types</div>
              <div className="flex flex-wrap gap-2">
                {["Land","Residential","Commercial"].map(t => {
                  const on = (bulk.property_types || []).includes(t);
                  return (
                    <button key={t} type="button" onClick={() => toggleBulkPType(t)} data-testid={`buyer-bulk-ptype-${t.toLowerCase()}`}
                      className={`text-[11px] mono uppercase tracking-[0.15em] px-2.5 py-1.5 rounded-sm border ${on ? "bg-emerald-500/15 border-emerald-500/50 text-emerald-300" : "border-zinc-800 text-zinc-500 hover:border-zinc-600"}`}>
                      {t}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Input label="Min price $" type="number" value={bulk.min_price} onChange={v => setBulk({ ...bulk, min_price: v })} testid="buyer-bulk-min" />
              <Input label="Max price $" type="number" value={bulk.max_price} onChange={v => setBulk({ ...bulk, max_price: v })} testid="buyer-bulk-max" />
            </div>
            <button type="submit" disabled={bulkBusy} data-testid="buyer-bulk-submit"
              className="text-xs mono uppercase tracking-[0.15em] px-3 py-2 rounded-sm bg-emerald-500 hover:bg-emerald-400 text-black font-semibold flex items-center gap-2 disabled:opacity-50">
              <Upload size={13} weight="bold" /> {bulkBusy ? "Importing…" : "Import buyers"}
            </button>
          </form>
        </div>

        <div className="lg:col-span-2">
          <div className="border border-zinc-800 rounded-sm bg-[#0b0b0e] overflow-hidden">
            <div className="px-4 py-2.5 border-b border-zinc-800 flex items-center justify-between">
              <span className="overline">Buyer list · {rows.length}</span>
              <span className="text-[10px] mono text-zinc-500">Auto-blast fires on HOT DEAL</span>
            </div>
            {rows.length === 0 ? (
              <div className="p-8 text-center text-sm text-zinc-500">No buyers yet. Add one, or paste a list into bulk import.</div>
            ) : (
              <ul className="divide-y divide-zinc-800/70" data-testid="buyer-list">
                {rows.map(b => (
                  <li key={b.id} className="p-4 flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold">{b.name} {b.company ? <span className="text-zinc-500 text-xs">· {b.company}</span> : null}</div>
                      <div className="text-xs mono text-zinc-400 truncate">{b.email}{b.phone ? ` · ${b.phone}` : ""}</div>
                      <div className="text-[11px] mono text-zinc-500 mt-1">
                        {b.states?.join(", ") || "no states"}
                        {b.zip_codes?.length ? ` · ZIPs ${b.zip_codes.join(", ")}` : ""}
                        {" · "}{b.property_types?.join(" + ") || "any type"} · ${Number(b.min_price||0).toLocaleString()}–${Number(b.max_price||0).toLocaleString()}
                        {b.active ? <span className="text-emerald-400"> · active</span> : <span className="text-zinc-500"> · paused</span>}
                      </div>
                      {b.notes && <div className="text-[11px] text-zinc-500 mt-1">{b.notes}</div>}
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <button onClick={() => edit(b)} className="text-[10px] mono uppercase tracking-[0.15em] px-2 py-1 rounded-sm border border-zinc-700 hover:border-zinc-500" data-testid={`buyer-edit-${b.id}`}>Edit</button>
                      <button onClick={() => remove(b.id)} className="text-[10px] mono uppercase tracking-[0.15em] px-2 py-1 rounded-sm border border-red-500/50 text-red-300 hover:bg-red-500/10" data-testid={`buyer-delete-${b.id}`}>
                        <Trash size={11} />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </AppShell>
  );
}

const Input = ({ label, value, onChange, type="text", required, placeholder, testid }) => (
  <div>
    <div className="overline mb-1">{label}</div>
    <input type={type} value={value} onChange={e => onChange(e.target.value)} required={required} placeholder={placeholder} data-testid={testid}
      className="w-full bg-[#18181b] hairline px-2 py-1.5 mono text-xs rounded-sm focus:outline-none focus:border-emerald-500" />
  </div>
);
